// Class board for U.S. Treasuries, Non-U.S. Govt Debt and Private Credit:
// headline tiles, issuer and chain leagues with share bars, then a sortable,
// filterable grid of every entry, read from GET /v1/classes/{class}/board
// (panther.class-board.v1) and refreshed every minute. Until the API publishes
// a class the screen says "coming soon" instead of showing zeros; a missing
// figure renders as "unavailable" (grid) or "—" (tiles). The headline value is
// the class total only when every entry is valued in full (classCoverage);
// otherwise it is a known value, prefixed "≥", with its coverage beside it. A
// protocol-level entry carries a note row: its value is protocol TVL from
// DefiLlama, not a token market cap. Supply-valued rows open the token panel
// (U-3) like the stock grids do.

import {
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
  type ColumnDef,
  type SortingState,
} from "@tanstack/react-table";
import { useEffect, useMemo, useState, type ReactNode } from "react";

import { RepresentedTile } from "./RepresentedTile";

import { API_BASE, NotPublished } from "../api";
import {
  chainLabel,
  chainLeague,
  classById,
  classCoverage,
  entriesByBasis,
  entryLinks,
  fetchClassBoard,
  entryYield,
  formatPrice,
  spreadText,
  hasTokenPanel,
  issuerLeague,
  priceBasisLabel,
  priceBasisMeaning,
  distributedEntries,
  ENTRY_CAVEATS,
  isRepresented,
  REPRESENTED_MEANING,
  representedLine,
  CREDIT_GROUPS,
  groupTotals,
  isEtfTracker,
  type GroupTotal,
  noPublishedNav,
  offeringPriceNote,
  entryNote,
  ownership,
  partialValueNote,
  subClassLabel,
  trackerStockId,
  unitLabel,
  totalText,
  type BoardClassId,
  type ClassBoard as ClassBoardData,
  type ClassEntry,
  type LeagueRow,
  type ValueBasis,
} from "../classes";
import { ago, amount, compactUsd, count, percent, signedPercent, utcTime } from "../format";
import { csvFileName, downloadCsv, toCsv, type CsvColumn } from "../csv";
import { FLOW_WINDOWS, flowFor, flowMethodText, flowText, flowTone, type FlowWindow } from "../flows";
import { fetchToken } from "../token-api";
import styles from "./ClassBoard.module.css";
import { HistoryPanel } from "./HistoryPanel";

const REFRESH_MS = 60_000;

type Status = "loading" | "ready" | "pending" | "error";

interface Load {
  board: ClassBoardData | null;
  status: Status;
  error: string | null;
}

/** Fetches the class board and refreshes it every minute; a failed refresh keeps the last board. */
function useClassBoard(classId: BoardClassId): Load {
  const [load, setLoad] = useState<Load>({ board: null, status: "loading", error: null });

  useEffect(() => {
    const controller = new AbortController();
    setLoad({ board: null, status: "loading", error: null });
    const read = () =>
      fetchClassBoard(classId, controller.signal)
        .then((board) => setLoad({ board, status: "ready", error: null }))
        .catch((e: unknown) => {
          if (controller.signal.aborted) return;
          setLoad((current) =>
            current.board
              ? current
              : {
                  board: null,
                  status: e instanceof NotPublished ? "pending" : "error",
                  error: e instanceof Error ? e.message : String(e),
                },
          );
        });
    void read();
    const timer = setInterval(() => void read(), REFRESH_MS);
    return () => {
      controller.abort();
      clearInterval(timer);
    };
  }, [classId]);

  return load;
}

// ---- tiles and leagues ----

/** The entry's plain summary (else its note's first sentence), with the full note behind "More". */
function EntryNote({ entry }: { entry: ClassEntry }) {
  const parts = entryNote(entry);
  if (!parts) return null;
  return (
    <>
      {" "}
      {parts.lead}
      {parts.rest ? (
        <details className={styles.noteMore}>
          <summary>More</summary>
          {parts.rest}
        </details>
      ) : null}
    </>
  );
}

function Tile({ label, value, note, sub }: { label: string; value: string; note?: string; sub?: string | null }) {
  return (
    <li className="tile">
      <span className="tile-label">{label}</span>
      <span className="tile-value" data-absent={value === "—"}>
        {value}
      </span>
      {note ? <span className="tile-note">{note}</span> : null}
      {sub ? <span className="tile-note">{sub}</span> : null}
    </li>
  );
}

const signedUsd = (value: number) => `${value >= 0 ? "+" : "−"}${compactUsd(Math.abs(value))}`;

/** A like-for-like change tile: market change first, coverage growth (products newly tracked) as a second line. */
function ChangeTile({ days, pct, usd, coverage }: { days: 7 | 30; pct: number; usd: number | null | undefined; coverage: number | null | undefined }) {
  return (
    <Tile
      label={`Change · ${days}d`}
      note={usd != null ? `${signedUsd(usd)} market, same products` : `same products as ${days === 7 ? "a week" : "30 days"} ago`}
      sub={coverage != null && coverage !== 0 ? `${signedUsd(coverage)} coverage ${coverage > 0 ? "growth" : "loss"}` : null}
      value={signedPercent(pct)}
    />
  );
}

/** A 7d or 30d change column; the column is hidden while every entry's change is null. */
function changeColumn(id: "change7d" | "change30d", header: string, pick: (e: ClassEntry) => { pct: number | null | undefined; usd: number | null | undefined }): ColumnDef<ClassEntry, unknown> {
  return {
    id,
    header,
    accessorFn: (e) => pick(e).pct ?? undefined,
    sortDescFirst: true,
    ...SORT_UNDEFINED_LAST,
    cell: ({ row }) => {
      const { pct, usd } = pick(row.original);
      if (pct === null || pct === undefined) return missing("no history yet", `Needs ${id === "change7d" ? "7" : "30"} days of daily snapshots`);
      return (
        <span className={pct > 0 ? styles.up : pct < 0 ? styles.down : undefined} title={usd != null ? `${usd >= 0 ? "+" : "−"}${compactUsd(Math.abs(usd))} in value` : undefined}>
          {signedPercent(pct)}
        </span>
      );
    },
  };
}

const sharePct = (share: number) => `${(share * 100).toFixed(share >= 0.1 ? 1 : 2)}%`;

function League({ title, note, rows, unit }: { title: string; note: string; rows: LeagueRow[]; unit: string }) {
  return (
    <section aria-label={title} className="panel">
      <header className="panel-head">
        <h2>{title}</h2>
        <span>{note}</span>
      </header>
      {rows.length === 0 ? (
        <p className="panel-empty">Not enough data yet.</p>
      ) : (
        <ol className={styles.league}>
          {rows.map((row, index) => (
            <li key={row.key}>
              <span className={styles.rank}>{row.valueUsd === null ? "" : index + 1}</span>
              <span className={styles.name}>
                <span className={styles.nameText}>{row.label}</span>
                <span className={styles.entries}>
                  {count(row.entries)} {row.entries === 1 ? unit : unit.replace(/y$/, "ie") + "s"}
                </span>
              </span>
              <span className={row.valueUsd === null ? styles.absentValue : styles.value}>
                {row.valueUsd === null ? "not valued" : compactUsd(row.valueUsd)}
              </span>
              <span className={styles.share}>{row.share === null ? "—" : sharePct(row.share)}</span>
              <span aria-hidden className={styles.bar}>
                <span style={{ width: `${row.share === null ? 0 : Math.min(100, row.share * 100)}%` }} />
              </span>
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}

// ---- entries grid ----

type Align = "left" | "right";

const SORT_UNDEFINED_LAST = { sortUndefined: "last" as const };

function Badge({ basis }: { basis: string | null }) {
  const label = priceBasisLabel(basis);
  if (!label) return null;
  return (
    <span className={styles.badge} title={priceBasisMeaning(basis)}>
      {label}
    </span>
  );
}

function Links({ entry }: { entry: ClassEntry }) {
  const links = entryLinks(entry);
  if (!links.length) return <span className={styles.absent}>—</span>;
  return (
    <span className={styles.links}>
      {links.map(({ label, url }) => (
        <a href={url} key={url} rel="noreferrer" target="_blank">
          {label}
        </a>
      ))}
    </span>
  );
}

/** A missing figure, with a short reason instead of "unavailable". */
const missing = (reason: string, title?: string) => (
  <span className={styles.absent} title={title}>
    {reason}
  </span>
);

/** The "All" button's figure: the sum of the groups, "≥" when any group is partly valued. */
function groupsTotalText(groups: readonly GroupTotal[]): string {
  const valued = groups.filter((g) => g.valueUsd !== null);
  if (!valued.length) return "—";
  const sum = valued.reduce((n, g) => n + (g.valueUsd as number), 0);
  return `${valued.length === groups.length && groups.every((g) => g.complete) ? "" : "≥ "}${compactUsd(sum)}`;
}

/** A private-equity product whose manager publishes no NAV Panther can read: said so, not a bare dash. */
const noNav = (
  <span className={styles.absent} title="The manager publishes no NAV Panther can read, so no value is shown; a mark is never invented">
    no published NAV
  </span>
);

/** An ETF tracker's price and supply: shown under Stocks, so here a dash with the reason, not "unavailable". */
const underStocks = (
  <span className={styles.muted} title="ETF tracker: priced and valued on the Stocks board, so it is not counted twice">
    —
  </span>
);

interface StockQuote {
  price: number | null;
  value: number | null;
}

/**
 * Latest price and market cap of the Stocks token each ETF tracker mirrors
 * (GET /v1/tokens/{id}). Display only: never added to a class figure. A tracker
 * whose token does not answer simply has no quote.
 */
function useStockQuotes(entries: readonly ClassEntry[]): Map<string, StockQuote> {
  const ids = useMemo(() => [...new Set(entries.map(trackerStockId).filter((id): id is string => id !== null))].sort().join(","), [entries]);
  const [quotes, setQuotes] = useState<Map<string, StockQuote>>(new Map());
  useEffect(() => {
    if (!ids) return;
    const controller = new AbortController();
    Promise.all(
      ids.split(",").map((id) =>
        fetchToken(id, controller.signal).then(
          (detail) => [id, { price: detail.latest?.price ?? null, value: detail.latest?.market_cap ?? null }] as const,
          () => null,
        ),
      ),
    ).then((results) => {
      if (!controller.signal.aborted) setQuotes(new Map(results.filter((r): r is NonNullable<typeof r> => r !== null)));
    });
    return () => controller.abort();
  }, [ids]);
  return quotes;
}

/** CSV columns for each grid column id, in grid order; Links has none. */
const ENTRY_CSV: Record<string, Array<CsvColumn<ClassEntry>>> = {
  token: [
    { header: "Symbol", value: (e) => e.symbol },
    { header: "Name", value: (e) => e.name },
    { header: "Token ID", value: (e) => e.token_id },
  ],
  issuer: [{ header: "Issuer", value: (e) => e.issuer.name }],
  subClass: [{ header: "Sub-class", value: (e) => subClassLabel(e.sub_class) }],
  chains: [{ header: "Chains", value: (e) => e.chains.map(chainLabel).join("; ") }],
  currency: [{ header: "Currency", value: (e) => e.currency }],
  price: [
    { header: "Price (USD)", value: (e) => e.price },
    { header: "Price basis", value: (e) => e.price_basis },
  ],
  supply: [{ header: "Supply", value: (e) => e.supply }],
  value: [
    { header: "Value (USD)", value: (e) => e.value_usd },
    { header: "Value basis", value: (e) => e.value_basis },
  ],
  change: [{ header: "24h change (fraction)", value: (e) => e.change_24h.price ?? e.change_24h.value_usd }],
  change7d: [{ header: "7d change (fraction)", value: (e) => e.change_7d }],
  change30d: [{ header: "30d change (fraction)", value: (e) => e.change_30d }],
  yield: [
    { header: "Yield (fraction)", value: (e) => entryYield(e).value },
    { header: "Yield source", value: (e) => entryYield(e).source },
  ],
  aum: [{ header: "Reported AUM (USD)", value: (e) => e.aum_usd_reported }],
  verified: [{ header: "Verified", value: (e) => e.verified }],
};

function EntriesGrid({
  entries,
  classLabel,
  csvBase,
  asOf,
  onSelect,
  groups = null,
}: {
  entries: ClassEntry[];
  classLabel: string;
  /** File name stem for the CSV export, e.g. "treasury-entries". */
  csvBase: string;
  asOf: string;
  onSelect: (tokenId: string) => void;
  /** Group totals for a class shown by group (credit); the group buttons replace the sub-class select. */
  groups?: GroupTotal[] | null;
}) {
  const quotes = useStockQuotes(entries);
  const [query, setQuery] = useState("");
  const [subClass, setSubClass] = useState("");
  const [chain, setChain] = useState("");
  const [basis, setBasis] = useState<"" | ValueBasis>("");
  const [flowWindow, setFlowWindow] = useState<FlowWindow>("1d");
  const [sorting, setSorting] = useState<SortingState>([{ id: "value", desc: true }]);

  const subClasses = useMemo(
    () =>
      [...new Set(entries.map((e) => e.sub_class).filter((s): s is string => s !== null))]
        .map((id) => ({ id, label: subClassLabel(id) ?? id }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [entries],
  );
  const chains = useMemo(
    () =>
      [...new Set(entries.flatMap((e) => e.chains))]
        .map((id) => ({ id, label: chainLabel(id) }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [entries],
  );
  const bases = useMemo(() => entriesByBasis(entries), [entries]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return entries.filter((e) => {
      if (subClass && e.sub_class !== subClass) return false;
      if (chain && !e.chains.includes(chain)) return false;
      if (basis && e.value_basis !== basis) return false;
      if (q && !`${e.symbol} ${e.name ?? ""} ${e.issuer.name}`.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [entries, query, subClass, chain, basis]);

  const columns = useMemo<Array<ColumnDef<ClassEntry, unknown>>>(
    () => [
      {
        id: "token",
        header: "Token",
        accessorFn: (e) => `${e.symbol} ${e.name ?? ""}`,
        meta: { align: "left" },
        cell: ({ row }) => {
          const e = row.original;
          return (
            <>
              {hasTokenPanel(e) ? (
                <button
                  className={styles.tokenButton}
                  onClick={(event) => {
                    event.currentTarget.focus();
                    onSelect(e.token_id);
                  }}
                  title={`Open ${e.symbol} details`}
                  type="button"
                >
                  {e.symbol}
                </button>
              ) : (
                <span className={styles.token} title={e.summary ?? e.notes ?? undefined}>
                  {e.symbol}
                </span>
              )}
              {isEtfTracker(e) ? (
                <span className={styles.tracker} title="A tokenized share of a commodity ETF, not a claim on the commodity. Counted under Stocks.">
                  ETF tracker
                </span>
              ) : isRepresented(e) ? (
                <span className={styles.represented} title={REPRESENTED_MEANING}>
                  Represented
                </span>
              ) : e.problems.length ? (
                <span className={styles.problems} title={e.problems.join("\n")}>
                  {e.problems.length} {e.problems.length === 1 ? "issue" : "issues"}
                </span>
              ) : null}
              {e.name ? (
                <span className={styles.tokenName} title={e.summary ?? undefined}>
                  {e.name}
                </span>
              ) : null}
              {unitLabel(e) ? <span className={styles.unit}>{unitLabel(e)}</span> : null}
              {e.instrument_type ? <span className={styles.instrument}>{subClassLabel(e.instrument_type)}</span> : null}
              {ownership(e) ? (
                <span className={ownership(e)!.shares ? styles.ownFund : styles.ownNotShares} title={ownership(e)!.meaning}>
                  {ownership(e)!.label}
                </span>
              ) : null}
              {ENTRY_CAVEATS[e.id] ? <span className={styles.caveat}>{ENTRY_CAVEATS[e.id]}</span> : null}
              {offeringPriceNote(e) ? <span className={styles.caveat}>{offeringPriceNote(e)}</span> : null}
            </>
          );
        },
      },
      {
        id: "issuer",
        header: "Issuer",
        accessorFn: (e) => e.issuer.name,
        meta: { align: "left" },
        cell: ({ row }) => {
          const { issuer } = row.original;
          return issuer.url ? (
            <a className={styles.issuer} href={issuer.url} rel="noreferrer" target="_blank">
              {issuer.name}
            </a>
          ) : (
            issuer.name
          );
        },
      },
      {
        id: "subClass",
        header: "Sub-class",
        accessorFn: (e) => subClassLabel(e.sub_class) ?? undefined,
        meta: { align: "left" },
        ...SORT_UNDEFINED_LAST,
        cell: ({ getValue }) => getValue<string | undefined>() ?? missing("—", "No sub-class in the registry"),
      },
      {
        id: "chains",
        header: "Chains",
        accessorFn: (e) => e.chains.map(chainLabel).join(", "),
        meta: { align: "left" },
        cell: ({ getValue }) => <span className={styles.muted}>{getValue<string>() || "—"}</span>,
      },
      { id: "currency", header: "Ccy", accessorFn: (e) => e.currency, meta: { align: "left" } },
      {
        id: "price",
        header: "Price (USD)",
        accessorFn: (e) => e.price ?? undefined,
        sortDescFirst: true,
        ...SORT_UNDEFINED_LAST,
        cell: ({ row }) => {
          const e = row.original;
          if (e.value_basis === "tvl" && e.price === null) return <span className={styles.absent} title="Valued by protocol TVL; no token price">n/a</span>;
          if (noPublishedNav(e)) return noNav;
          if (isEtfTracker(e) && e.price === null) {
            const quote = quotes.get(trackerStockId(e)!);
            return quote?.price != null ? (
              <>
                {formatPrice(quote.price, "USD")}
                <span className={styles.tracker} title="The Stocks token's latest price">
                  Stocks
                </span>
              </>
            ) : (
              underStocks
            );
          }
          const foreign = e.price_currency !== "USD" && e.native_price !== null;
          return (
            <>
              {e.price === null ? missing("no price", "No price source answered in the last collection") : formatPrice(e.price, "USD")}
              <Badge basis={e.price_basis} />
              {foreign ? (
                <span
                  className={styles.native}
                  title={e.fx_rate === null ? "FX rate unavailable: no USD price" : `At ${e.fx_rate.toFixed(4)} USD per ${e.price_currency}`}
                >
                  {formatPrice(e.native_price, e.price_currency)}
                </span>
              ) : null}
            </>
          );
        },
      },
      {
        id: "supply",
        header: "Supply",
        accessorFn: (e) => e.supply ?? undefined,
        sortDescFirst: true,
        ...SORT_UNDEFINED_LAST,
        cell: ({ row }) => {
          const e = row.original;
          if (e.supply === null && isEtfTracker(e)) return underStocks;
          if (e.supply === null)
            return e.value_basis === "tvl" && !e.deployments.length ? <span className={styles.absent}>n/a</span> : missing("not read", "Supply not read on its chains in the last collection");
          return (
            <>
              {amount(e.supply)}
              {e.supply_complete === false ? (
                <span className={styles.sub} title="Supply read on only some of this token's chains: a floor, not the full supply">
                  partial
                </span>
              ) : null}
            </>
          );
        },
      },
      {
        id: "value",
        header: "Value (USD)",
        accessorFn: (e) => e.value_usd ?? undefined,
        sortDescFirst: true,
        ...SORT_UNDEFINED_LAST,
        cell: ({ row }) => {
          const e = row.original;
          if (e.value_usd === null && noPublishedNav(e)) return noNav;
          if (e.value_usd === null && isEtfTracker(e)) {
            const quote = quotes.get(trackerStockId(e)!);
            return (
              <>
                {quote?.value != null ? compactUsd(quote.value) : null}
                <span className={styles.tracker} title="Valued on the Stocks board; shown here for reference and left out of this class's totals, so it is never counted twice">
                  counted under Stocks
                </span>
              </>
            );
          }
          if (e.value_usd === null) return e.price === null ? missing("no price") : e.supply === null ? missing("no supply read") : missing("not valued");
          const partial = e.value_basis === "market_cap" && e.supply_complete === false;
          return (
            <>
              {partial ? `≥ ${compactUsd(e.value_usd)}` : compactUsd(e.value_usd)}
              {isRepresented(e) ? (
                <span className={styles.tracker} title={REPRESENTED_MEANING}>
                  not in total
                </span>
              ) : null}
              {e.value_basis === "tvl" ? (
                <span className={styles.badgeTvl} title="Protocol TVL from DefiLlama, not a token market cap">
                  TVL
                </span>
              ) : null}
            </>
          );
        },
      },
      {
        id: "change",
        header: "24h",
        accessorFn: (e) => e.change_24h.price ?? e.change_24h.value_usd ?? undefined,
        sortDescFirst: true,
        ...SORT_UNDEFINED_LAST,
        cell: ({ row }) => {
          const { change_24h: change } = row.original;
          const v = change.price ?? change.value_usd;
          if (v === null) return missing("no 24h yet", "Builds after a day of history");
          return (
            <>
              <span className={v > 0 ? styles.up : v < 0 ? styles.down : undefined}>{signedPercent(v)}</span>
              {change.price === null ? (
                <span className={styles.sub} title="Change in value (USD); the entry has no token price">
                  value
                </span>
              ) : null}
            </>
          );
        },
      },
      changeColumn("change7d", "7d", (e) => ({ pct: e.change_7d, usd: e.change_7d_usd })),
      changeColumn("change30d", "30d", (e) => ({ pct: e.change_30d, usd: e.change_30d_usd })),
      {
        id: "netFlow",
        header: `Net flow · ${flowWindow}`,
        accessorFn: (e) => flowFor(e, flowWindow) ?? undefined,
        sortDescFirst: true,
        ...SORT_UNDEFINED_LAST,
        cell: ({ row }) => {
          const flow = flowFor(row.original, flowWindow);
          if (flow === null) {
            return row.original.value_basis === "tvl"
              ? missing("TVL, no supply", "Valued by protocol TVL: no supply to measure flows from")
              : missing(flowWindow === "1d" ? "not measured" : "no history yet", flowWindow === "1d" ? "Needs a daily snapshot of the day before" : `Needs ${flowWindow} of daily snapshots`);
          }
          const tone = flowTone(flow);
          return (
            <span className={tone === "up" ? styles.up : tone === "down" ? styles.down : undefined} title={flowMethodText(row.original.flow_method)}>
              {flowText(flow)}
            </span>
          );
        },
      },
      {
        id: "yield",
        header: "Yield",
        accessorFn: (e) => entryYield(e).value ?? undefined,
        sortDescFirst: true,
        ...SORT_UNDEFINED_LAST,
        cell: ({ row }) => {
          const y = entryYield(row.original);
          if (y.value === null) {
            const e = row.original;
            const reason = e.yield_method === "nav_accrual" ? "building" : e.yield_method === "issuer" ? "not published" : e.yield_method === "none" ? "n/a" : "—";
            return missing(reason, y.title ?? undefined);
          }
          return (
            <span title={y.title ?? undefined}>
              {percent(y.value)}
              <span className={styles.sub}>{y.source === "issuer" ? "issuer" : y.source === "nav_30d" ? "30d" : "7d"}</span>
            </span>
          );
        },
      },
      {
        id: "aum",
        header: "Reported AUM",
        accessorFn: (e) => e.aum_usd_reported ?? undefined,
        sortDescFirst: true,
        ...SORT_UNDEFINED_LAST,
        cell: ({ getValue }) => {
          const v = getValue<number | undefined>();
          return v === undefined ? missing("not reported", "The issuer reports no AUM") : compactUsd(v);
        },
      },
      {
        id: "verified",
        header: "Verified",
        accessorFn: (e) => (e.verified === null ? undefined : e.verified ? 1 : 0),
        sortDescFirst: true,
        ...SORT_UNDEFINED_LAST,
        meta: { align: "left" },
        cell: ({ row }) =>
          row.original.verified === null ? (
            <span className={styles.absent} title="No on-chain deployment to verify">
              —
            </span>
          ) : row.original.verified ? (
            <span className={styles.verified} title="Every deployment confirmed against the issuer's own material">
              verified
            </span>
          ) : (
            <span className={styles.unverified} title="At least one deployment not yet confirmed as the issuer's">
              unverified
            </span>
          ),
      },
      {
        id: "links",
        header: "Links",
        enableSorting: false,
        meta: { align: "left" },
        cell: ({ row }) => <Links entry={row.original} />,
      },
    ],
    [onSelect, quotes, flowWindow],
  );

  // The 7d / 30d columns appear once any entry has that change (7d from about 2026-10-11, 30d from 2026-11-03).
  const columnVisibility = useMemo(
    () => ({
      change7d: entries.some((e) => e.change_7d != null),
      change30d: entries.some((e) => e.change_30d != null),
    }),
    [entries],
  );

  const table = useReactTable({
    data: filtered,
    columns,
    state: { sorting, columnVisibility },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  const rows = table.getRowModel().rows;

  /** Exactly what the grid shows: its filtered, sorted rows and its visible columns. */
  function exportCsv(): void {
    const columns = table.getVisibleLeafColumns().flatMap((column): Array<CsvColumn<ClassEntry>> =>
      column.id === "netFlow"
        ? [
            { header: `Net flow ${flowWindow} (USD)`, value: (e) => flowFor(e, flowWindow) },
            { header: "Flow method", value: (e) => e.flow_method },
          ]
        : (ENTRY_CSV[column.id] ?? []),
    );
    downloadCsv(csvFileName(csvBase, asOf), toCsv(rows.map((row) => row.original), columns));
  }
  const columnCount = table.getVisibleLeafColumns().length;
  const filtering = Boolean(query || subClass || chain || basis);

  function clear(): void {
    setQuery("");
    setSubClass("");
    setChain("");
    setBasis("");
  }

  return (
    <section aria-label={`${classLabel} entries`} className={styles.grid}>
      {groups && groups.length > 1 ? (
        <div aria-label={`${classLabel} group`} className={styles.groups} role="group">
          <button aria-pressed={subClass === ""} className={styles.group} onClick={() => setSubClass("")} type="button">
            All
            <span className={styles.groupFigure}>
              {groupsTotalText(groups)} · {count(groups.reduce((n, g) => n + g.entries, 0))}
            </span>
          </button>
          {groups.map((g) => (
            <button aria-pressed={subClass === g.id} className={styles.group} key={g.id} onClick={() => setSubClass(subClass === g.id ? "" : g.id)} type="button">
              {g.label}
              <span className={styles.groupFigure}>
                {g.valueUsd === null ? "—" : `${g.complete ? "" : "≥ "}${compactUsd(g.valueUsd)}`} · {count(g.entries)}
              </span>
            </button>
          ))}
        </div>
      ) : null}
      <div className={styles.toolbar}>
        <input
          aria-label="Filter by token, name or issuer"
          className={styles.search}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="token, name or issuer…"
          type="search"
          value={query}
        />
        {subClasses.length > 1 && !(groups && groups.length > 1) ? (
          <select aria-label="Sub-class" className={styles.select} onChange={(event) => setSubClass(event.target.value)} value={subClass}>
            <option value="">All sub-classes</option>
            {subClasses.map((s) => (
              <option key={s.id} value={s.id}>
                {s.label}
              </option>
            ))}
          </select>
        ) : null}
        {chains.length > 1 ? (
          <select aria-label="Chain" className={styles.select} onChange={(event) => setChain(event.target.value)} value={chain}>
            <option value="">All chains</option>
            {chains.map((c) => (
              <option key={c.id} value={c.id}>
                {c.label}
              </option>
            ))}
          </select>
        ) : null}
        {bases.market_cap && bases.tvl ? (
          <select aria-label="Value basis" className={styles.select} onChange={(event) => setBasis(event.target.value as "" | ValueBasis)} value={basis}>
            <option value="">Market cap and TVL</option>
            <option value="market_cap">Market cap only</option>
            <option value="tvl">Protocol TVL only</option>
          </select>
        ) : null}
        <select aria-label="Net flow window" className={styles.select} onChange={(event) => setFlowWindow(event.target.value as FlowWindow)} value={flowWindow}>
          {FLOW_WINDOWS.map(([id, label]) => (
            <option key={id} value={id}>
              Net flow · {label}
            </option>
          ))}
        </select>
        <button className={styles.csv} disabled={!rows.length} onClick={exportCsv} type="button">
          Download CSV
        </button>
        <span className={styles.matchCount}>
          {rows.length} of {entries.length} entries
          {filtering ? (
            <button className={styles.clear} onClick={clear} type="button">
              clear filters
            </button>
          ) : null}
        </span>
      </div>
      <div aria-label={`${classLabel} entry data`} className={styles.scroll} role="region" tabIndex={0}>
        <table className={styles.table}>
          <thead>
            {table.getHeaderGroups().map((group) => (
              <tr key={group.id}>
                {group.headers.map((header) => {
                  const sorted = header.column.getIsSorted();
                  const align = ((header.column.columnDef.meta as { align?: Align } | undefined)?.align ?? "right") satisfies Align;
                  const label = String(header.column.columnDef.header);
                  return (
                    <th
                      aria-sort={sorted === "asc" ? "ascending" : sorted === "desc" ? "descending" : "none"}
                      data-align={align}
                      key={header.id}
                      scope="col"
                    >
                      {header.column.getCanSort() ? (
                        <button
                          className={sorted ? styles.sortSorted : styles.sort}
                          onClick={header.column.getToggleSortingHandler()}
                          title={`Sort by ${label}`}
                          type="button"
                        >
                          {flexRender(header.column.columnDef.header, header.getContext())}
                          <span aria-hidden className={styles.sortMark}>
                            {sorted === "desc" ? "↓" : sorted === "asc" ? "↑" : "↕"}
                          </span>
                        </button>
                      ) : (
                        <span className={styles.plainHead}>{flexRender(header.column.columnDef.header, header.getContext())}</span>
                      )}
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td className={styles.emptyRow} colSpan={columnCount}>
                  {entries.length === 0 ? "No entries on this board yet." : "No entry matches these filters."}
                </td>
              </tr>
            ) : (
              rows.map((row) => {
                const e = row.original;
                const tvl = e.value_basis === "tvl";
                const represented = isRepresented(e);
                return [
                  <tr className={tvl || represented ? styles.hasNote : undefined} key={e.id}>
                    {row.getVisibleCells().map((cell) => {
                      const align = ((cell.column.columnDef.meta as { align?: Align } | undefined)?.align ?? "right") satisfies Align;
                      return (
                        <td data-align={align} key={cell.id}>
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </td>
                      );
                    })}
                  </tr>,
                  represented ? (
                    <tr className={styles.noteRow} key={`${e.id}-note`}>
                      <td colSpan={columnCount}>
                        {e.symbol} is represented, not distributed: an on-chain record of off-chain assets that investors
                        cannot hold or transfer. Its value is listed here and summed apart; it is left out of the class
                        total, counts and flows.
                        <EntryNote entry={e} />
                      </td>
                    </tr>
                  ) : tvl ? (
                    <tr className={styles.noteRow} key={`${e.id}-note`}>
                      <td colSpan={columnCount}>
                        {e.symbol}: value is protocol TVL from DefiLlama, not a token market cap.
                        <EntryNote entry={e} />
                      </td>
                    </tr>
                  ) : null,
                ];
              })
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}

// ---- states ----

function State({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section aria-live="polite" className={styles.state}>
      <h2 className={styles.stateTitle}>{title}</h2>
      {children}
    </section>
  );
}

// ---- the board ----

export function ClassBoard({ classId, onSelectToken }: { classId: BoardClassId; onSelectToken: (tokenId: string) => void }) {
  const cls = classById(classId);
  const { board, status, error } = useClassBoard(classId);
  const issuers = useMemo(() => (board ? issuerLeague(board) : []), [board]);
  const chains = useMemo(() => (board ? chainLeague(board) : []), [board]);

  if (!board) {
    if (status === "pending" || status === "error") {
      const endpoint = `${API_BASE.replace(/^https?:\/\//, "")}/v1/classes/${classId}/board`;
      return (
        <>
          <p className="venue-meta">
            {cls.label} · {status === "pending" ? "coming soon" : <span className="error">data unavailable right now: {error}</span>}
          </p>
          <p className={styles.blurb}>{cls.blurb}</p>
          <State title={status === "pending" ? "Coming soon" : "Board unavailable"}>
            <p>
              {status === "pending" ? (
                <>
                  The {cls.label} board is not published yet: the API has no <code>{endpoint}</code> to show. It appears
                  here as soon as the first collection of this class finishes.
                </>
              ) : (
                <>
                  The API could not serve <code>{endpoint}</code> just now. This screen retries every minute.
                </>
              )}{" "}
              Nothing on this screen is a zero in disguise.
            </p>
          </State>
        </>
      );
    }
    return <p className="venue-meta">Loading…</p>;
  }

  const { totals, entries } = board;
  // Represented entries (DQ-12b) are listed in the grid but left out of every figure below.
  const distributed = distributedEntries(entries);
  const represented = representedLine(totals);
  const coverage = classCoverage(board);
  const bases = entriesByBasis(distributed);
  const basisTile =
    bases.market_cap && bases.tvl ? "Mixed" : bases.tvl ? "Protocol TVL" : bases.market_cap ? "Market cap" : "—";
  const valuedIssuers = issuers.filter((row) => row.valueUsd !== null);
  const valuedChains = chains.filter((row) => row.valueUsd !== null);
  const topIssuer = valuedIssuers[0];
  const topChain = valuedChains[0];
  const byBasisNote = [
    totals.by_basis.market_cap !== null ? `${compactUsd(totals.by_basis.market_cap)} market cap` : null,
    totals.by_basis.tvl !== null ? `${compactUsd(totals.by_basis.tvl)} protocol TVL` : null,
  ]
    .filter((part): part is string => part !== null)
    .join(" + ");
  const missing = coverage.entries - coverage.valued;
  const coverageNote = [
    missing ? `${count(missing)} without a value, left out` : null,
    coverage.trackers ? `${count(coverage.trackers)} ETF ${coverage.trackers === 1 ? "tracker" : "trackers"} counted under Stocks` : null,
    coverage.partialSupply ? `${count(coverage.partialSupply)} with supply read on only some chains` : null,
  ]
    .filter((part): part is string => part !== null)
    .join(" · ");
  const chainValue = valuedChains.reduce((sum, row) => sum + (row.valueUsd ?? 0), 0);

  return (
    <>
      <p className="venue-meta">
        <strong>{count(totals.entries)}</strong> {totals.entries === 1 ? "entry" : "entries"}
        {represented ? ` + ${count(represented.entries)} represented` : ""} from{" "}
        <strong>{count(issuers.length)}</strong> {issuers.length === 1 ? "issuer" : "issuers"} on{" "}
        <strong>{count(chains.length)}</strong> {chains.length === 1 ? "chain" : "chains"} · as of{" "}
        <strong>{utcTime(board.as_of)}</strong> · updated {ago(board.generated_at)} · refreshes every 30 min
      </p>
      <p className={styles.blurb}>{cls.blurb}</p>

      <ul className="tiles">
        <RepresentedTile
          complete={coverage.complete}
          label={coverage.complete ? "Total value" : "Known value"}
          line={represented}
          value={totalText(totals.value_usd, coverage.complete, compactUsd)}
          note={
            totals.value_usd === null
              ? "no entry valued yet"
              : coverage.complete
                ? byBasisNote || "every entry valued"
                : partialValueNote(coverage, count) ?? undefined
          }
        />
        {totals.change_7d != null ? (
          <ChangeTile coverage={totals.coverage_change_7d_usd} days={7} pct={totals.change_7d} usd={totals.change_7d_usd} />
        ) : null}
        {totals.change_30d != null ? (
          <ChangeTile coverage={totals.coverage_change_30d_usd} days={30} pct={totals.change_30d} usd={totals.change_30d_usd} />
        ) : null}
        {flowFor(totals, "7d") !== null ? (
          <Tile
            label="Net flows · 7d"
            note={[flowMethodText(totals.flow_method)?.toLowerCase() ?? "units issued minus redeemed", flowFor(totals, "1d") !== null ? `${flowText(flowFor(totals, "1d"))} in the last day` : null]
              .filter((part): part is string => part !== null)
              .join(" · ")}
            value={flowText(flowFor(totals, "7d"))}
          />
        ) : null}
        {totals.avg_yield != null ? (
          <Tile
            label="Yield vs SOFR"
            value={percent(totals.avg_yield)}
            note={[
              totals.sofr != null ? `SOFR ${percent(totals.sofr)}${totals.sofr_apy != null ? ` (${percent(totals.sofr_apy)} APY)` : ""}` : null,
              totals.avg_yield_spread != null ? `spread ${spreadText(totals.avg_yield_spread)}` : null,
              totals.avg_yield_coverage != null ? `covers ${Math.round(totals.avg_yield_coverage * 100)}% of value` : null,
            ]
              .filter((part): part is string => part !== null)
              .join(" · ")}
          />
        ) : null}
        <Tile
          label="Coverage · valued / listed"
          value={coverage.share === null ? "—" : `${count(coverage.valued)} / ${count(coverage.entries)}`}
          note={coverage.complete ? "every entry valued in full" : coverageNote || "—"}
        />
        <Tile
          label="Issuers"
          value={issuers.length ? count(issuers.length) : "—"}
          note={topIssuer?.share != null ? `${topIssuer.label} holds ${sharePct(topIssuer.share)} of known value` : "with entries on this board"}
        />
        <Tile
          label="Chains"
          value={chains.length ? count(chains.length) : "—"}
          note={topChain?.share != null ? `${topChain.label} carries ${sharePct(topChain.share)} of chain value` : "with entries on this board"}
        />
        <Tile
          label="Value basis"
          value={basisTile}
          note={
            bases.market_cap && bases.tvl
              ? `${byBasisNote || `${count(bases.market_cap)} by market cap · ${count(bases.tvl)} by protocol TVL`}`
              : bases.tvl
                ? "protocol TVL from DefiLlama; no token prices"
                : "supply × price per token"
          }
        />
      </ul>

      <HistoryPanel chartTitle={`${cls.label} value, USD`} subject={{ kind: "class", classApiId: cls.apiId }} title="Value over time" />
      {classId === "treasury" ? (
        <HistoryPanel
          chartTitle="Secured Overnight Financing Rate"
          footnote="source: Federal Reserve Bank of New York · simple act/360 overnight rate, by effective date"
          subject={{ kind: "sofr" }}
          title="SOFR"
          unit="rate"
        />
      ) : null}

      <div className={`panels ${styles.leagues}`}>
        <League title="Issuer league" note="share of known class value" rows={issuers} unit="entry" />
        <League
          title="Chain breakdown"
          note={
            bases.tvl && valuedChains.length
              ? `supply × price per chain; ${compactUsd(chainValue)} attributed, protocol TVL is not split by chain`
              : "supply × price per chain"
          }
          rows={chains}
          unit="entry"
        />
      </div>

      <EntriesGrid
        asOf={board.as_of}
        classLabel={cls.label}
        csvBase={`${classId}-entries`}
        entries={entries}
        groups={classId === "credit" ? groupTotals(distributed, CREDIT_GROUPS) : null}
        onSelect={onSelectToken}
      />
    </>
  );
}
